import _mergeJSXProps from "@vue/babel-helper-vue-jsx-merge-props";
var __decorate = this && this.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
    r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
    d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isEqual, throttle } from "lodash-es";
import Vue from 'vue';
import Component from 'vue-class-component';
import ErrorBoundary from '../comps/ErrorBoundary';
import Portal from '../comps/Portal/Portal';
import { CalmComputedGetter, getCompPathParentInfo, getOffsetRect } from "../../lib/utils";
import { CTRL_KEY, DND_TYPE_LAYOUT, INJECT_UICORE_BUS, INJECT_UICORE_DESIGNER, ACTIONBAR_IN_BOTTOM_MAX_HEIGHT } from "../../lib/utils/consts";
import { isInsideRect, rect2css } from "../../lib/utils/styling";
import CompActionBar from './CompActionBar';
import AbsolutePositioningTool from './AbsolutePositioningTool';
import { useDragGhost } from './DragGhost';
import { getVNodeFactoryState } from '../renderer/utils';
import mergeClassName from '@tencent/data-schema/lib/utils/mergeClassName';
let CompSkeleton = class CompSkeleton extends Vue {
  constructor() {
    super(...arguments);
    this.weirdFlagForCompInstance = true;
    // 在 syncStyle() 时候更新
    this.compElement = undefined;
    this.isPositionAbsolute = false;
    this.isExtremelySmall = false;
    // 这个需要动态刷新的
    this.ownerSlotContainerElement = null;
    this.isVisible = false;
    this.skeletonStyle = {};
    this.actionBarWrapperStyle = '';
    /** actionBar是否放置于组件底部 */
    this.actionBarInBottom = false;
  }
  get renderer() {
    return this.designer.renderer;
  }
  get compInstance() {
    var _this$renderer;
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.weirdFlagForCompInstance;
    return (_this$renderer = this.renderer) === null || _this$renderer === void 0 ? void 0 : _this$renderer.getRefByPath(this.compPath);
  }
  getCompElement() {
    var _this$compInstance;
    return (_this$compInstance = this.compInstance) === null || _this$compInstance === void 0 ? void 0 : _this$compInstance.$el;
  }
  get isSelected() {
    return this.highlightType === 'selected';
  }
  get isHovering() {
    return this.highlightType === 'hovering';
  }
  get actionBarStatus() {
    if (!this.isVisible) return 'none';
    if (!this.isHovering && !this.isSelected) return 'none';
    /* 拖拽期间隐藏，但是不要移除DOM，因为这个本身就可以是拖拽源 */
    if (this.designer.currentOperation) return 'transparent';
    return 'visible';
  }
  get layout() {
    return this.designer.getCompAtPath(this.compPath);
  }
  get compPathInfo() {
    return getCompPathParentInfo(this.compPath);
  }
  get compType() {
    var _this$layout;
    return (_this$layout = this.layout) === null || _this$layout === void 0 ? void 0 : _this$layout.type;
  }
  get compMeta() {
    return this.uc.components[this.compType];
  }
  get isRoot() {
    return !this.compPath;
  }
  get isNestedRoot() {
    var _this$renderer2, _this$vNodeFactory;
    return !((_this$renderer2 = this.renderer) !== null && _this$renderer2 !== void 0 && _this$renderer2.ucIsRoot) && !!((_this$vNodeFactory = this.vNodeFactory) !== null && _this$vNodeFactory !== void 0 && _this$vNodeFactory.isScopeRoot);
  }
  get isUnselectable() {
    var _this$compMeta;
    return !!((_this$compMeta = this.compMeta) !== null && _this$compMeta !== void 0 && _this$compMeta.isUnselectable);
  }
  get hasError() {
    var _this$renderer3;
    return !!((_this$renderer3 = this.renderer) !== null && _this$renderer3 !== void 0 && _this$renderer3.ucLayoutErrors[this.compPath]);
  }
  get hasDisplayCondition() {
    return !!this.layout && !!(this.layout.renderCondition || this.layout.displayCondition);
  }
  get vNodeFactoryState() {
    return getVNodeFactoryState(this.renderer, this.compPath);
  }
  get vNodeFactory() {
    var _this$vNodeFactorySta;
    return (_this$vNodeFactorySta = this.vNodeFactoryState) === null || _this$vNodeFactorySta === void 0 ? void 0 : _this$vNodeFactorySta.factory;
  }
  get ownerSlotMeta() {
    var _this$compPathInfo, _parentFactoryState$g;
    const slotId = (_this$compPathInfo = this.compPathInfo) === null || _this$compPathInfo === void 0 ? void 0 : _this$compPathInfo.slotId;
    if (!slotId) return null; // 根插槽的情况
    const parentFactoryState = getVNodeFactoryState(this.designer.renderer, this.compPathInfo.parentCompPath);
    return parentFactoryState === null || parentFactoryState === void 0 || (_parentFactoryState$g = parentFactoryState.getNormalSlots()) === null || _parentFactoryState$g === void 0 ? void 0 : _parentFactoryState$g.find(X => X.id === slotId);
  }
  getOwnerSlotContainerElement() {
    var _this$ownerSlotMeta, _this$ownerSlotMeta$g;
    const {
      compPathInfo
    } = this;
    if (!compPathInfo) return null;
    return ((_this$ownerSlotMeta = this.ownerSlotMeta) === null || _this$ownerSlotMeta === void 0 || (_this$ownerSlotMeta$g = _this$ownerSlotMeta.getSlotContainerElement) === null || _this$ownerSlotMeta$g === void 0 ? void 0 : _this$ownerSlotMeta$g.call(_this$ownerSlotMeta)) || null;
  }
  get isOwnerSlotFrozen() {
    return !this.ownerSlotContainerElement;
  }
  /** 组件在绝对定位布局里 */
  get isInAbsoluteSlot() {
    var _this$ownerSlotMeta2;
    return ((_this$ownerSlotMeta2 = this.ownerSlotMeta) === null || _this$ownerSlotMeta2 === void 0 ? void 0 : _this$ownerSlotMeta2.position) === 'absolute';
  }
  /** 是否允许拖拽改这个组件在 DOM 结构里的位置（不是绝对定位） */
  get isDraggable() {
    const {
      isRoot,
      isUnselectable
    } = this;
    const draggable = !isRoot && !isUnselectable && !this.isOwnerSlotFrozen;
    return draggable;
  }
  get DesignTool() {
    var _this$compMeta2;
    return (_this$compMeta2 = this.compMeta) === null || _this$compMeta2 === void 0 ? void 0 : _this$compMeta2.meta.designTool;
  }
  get compName() {
    return this.uc.getLayoutName(this.layout);
  }
  get className() {
    let ans = '';
    if (this.isHovering) ans += ' isHovering';
    if (this.isSelected) ans += ' isSelected';
    if (this.isDraggable) ans += ' isDraggable';
    if (this.isExtremelySmall) ans += ' isExtremelySmall';
    if (this.isNestedRoot) ans += ' isNestedRoot';
    if (this.hasError) ans += ' hasError';
    if (this.isTempHidden) ans += ' isTempHidden';
    return ans;
  }
  get isTempHidden() {
    if (!this.isVisible) return false;
    if (this.isNestedRoot) {
      // 如果是触发器 也要保持遮罩
      if (this.designer.compPath.includes('@trigger')) return false;
      const allOtherSkeletons = Array.from(this.designer.editZoneState.compSkeletons).filter(x => x && x !== this);
      if (!allOtherSkeletons.length) return false;
      const style = this.skeletonStyle;
      const selfRect = {
        x: parseFloat(style.left),
        y: parseFloat(style.top),
        width: parseFloat(style.width),
        height: parseFloat(style.height)
      };
      // 如果有一个框飘出去了，就不显示巨大的灰色遮罩了
      return allOtherSkeletons.some(item => {
        const style = item.skeletonStyle;
        const tolerance = 4;
        const subLeft = parseFloat(style.left) + tolerance;
        const subTop = parseFloat(style.top) + tolerance;
        const subRight = parseFloat(style.width) + subLeft - tolerance * 2;
        const subBottom = parseFloat(style.height) + subTop - tolerance * 2;
        return !(isInsideRect(subLeft, subTop, selfRect) && isInsideRect(subRight, subBottom, selfRect));
      });
    }
    return false;
  }
  syncStyle() {
    const compElement = this.getCompElement();
    this.compElement = compElement;
    if (!compElement || compElement.nodeType !== Node.ELEMENT_NODE) {
      this.isVisible = false;
      return;
    }
    // 重新更新插槽 DOM 信息
    const ownerSlotContainerElement = this.getOwnerSlotContainerElement();
    if (ownerSlotContainerElement !== this.ownerSlotContainerElement) {
      this.ownerSlotContainerElement = ownerSlotContainerElement;
    }
    // 同步尺寸信息
    const editZoneEl = this.designer.$refs.editZone.$el;
    const rect = getOffsetRect(compElement, editZoneEl);
    if (rect.width === 0 && rect.height === 0) {
      this.isVisible = false;
      return;
    }
    const {
      minComponentMeasurement
    } = this.designer.uc.config;
    const extraWidth = Math.max(0, minComponentMeasurement - rect.width);
    const extraHeight = Math.max(0, minComponentMeasurement - rect.height);
    const isExtremelySmall = rect.height < 1 || rect.width < 1;
    const absoluteRect = {
      x: rect.left - extraWidth / 2,
      y: rect.top - extraHeight / 2,
      width: rect.width + extraWidth,
      height: rect.height + extraHeight
    };
    const newStyle = rect2css(absoluteRect);
    const computedStyle = getComputedStyle(compElement);
    const isPositionAbsolute = ['absolute', 'fixed'].includes(computedStyle.position);
    if (!isEqual(this.skeletonStyle, newStyle)) this.skeletonStyle = newStyle;
    this.isVisible = true;
    this.isExtremelySmall = isExtremelySmall;
    this.isPositionAbsolute = isPositionAbsolute;
    if (this.actionBarStatus !== 'none') this.$nextTick(() => {
      // actionBarWrapper 会被传送到 designer 下面挂着
      const barWrapper = this.$refs.actionBarWrapper;
      let newStyle = '';
      if (barWrapper) {
        const ezWidth = editZoneEl.offsetWidth;
        const ezHeight = editZoneEl.offsetHeight;
        const barWidth = barWrapper.offsetWidth;
        const barHeight = barWrapper.offsetHeight;
        let left = rect.left + rect.width - barWidth - editZoneEl.scrollLeft;
        let top = rect.top - barWrapper.offsetHeight - editZoneEl.scrollTop;
        if (left + barWidth > ezWidth) left = ezWidth - barWidth;
        if (left < 0) left = 0;
        if (top + barHeight > ezHeight) top = ezHeight - barHeight;
        if (top < 0) {
          // actionBar上面冒了 要调整到下面
          const bottomTop = absoluteRect.height + absoluteRect.y + 8;
          top = bottomTop;
          this.actionBarInBottom = true;
          // 但是如果组件太高了遮挡上面一点应该也无所谓
          if (bottomTop > ACTIONBAR_IN_BOTTOM_MAX_HEIGHT) {
            top = 0;
            this.actionBarInBottom = false;
          }
        } else {
          this.actionBarInBottom = false;
        }
        newStyle = `left: ${left}px; top: ${top}px; max-width: ${ezWidth}px; opacity: 1`;
      }
      if (newStyle !== this.actionBarWrapperStyle) this.actionBarWrapperStyle = newStyle;
    });
  }
  handleDesignToolMousedown() {
    // 在 designTool 发生鼠标事件时，如果当前组件没有被选中，就选中他
    if (!this.isSelected) this.designer.compPath = this.compPath;
  }
  created() {
    const syncStyleDebounced = throttle(() => {
      this.syncStyle();
    }, 100);
    this.$watch(() => this.className, syncStyleDebounced);
    this.$watch(() => this.compInstance, syncStyleDebounced);
    this.$watch(() => this.isNestedRoot && this.designer.scope, syncStyleDebounced);
    this.$watch(() => {
      var _this$designer$pagele;
      return (_this$designer$pagele = this.designer.pagelet) === null || _this$designer$pagele === void 0 ? void 0 : _this$designer$pagele.layout;
    }, syncStyleDebounced, {
      deep: true
    });
    // 编辑器缩放后刷新style
    this.$watch(() => this.designer.zoom, syncStyleDebounced);
    this.$watch(() => this.ownerSlotMeta, syncStyleDebounced);
    this.syncStyleDebounced = syncStyleDebounced;
    // FIXME
    // 组件拖拽进 uc-slot 生成的插槽后，有bug导致组件实例拿不到
    // 这里先粗暴处理下
    let weirdTimer;
    this.$watch(() => !!this.compInstance, hasInstance => {
      if (hasInstance) {
        clearTimeout(weirdTimer);
        weirdTimer = 0;
        return;
      }
      weirdTimer = setTimeout(() => {
        this.weirdFlagForCompInstance = !this.weirdFlagForCompInstance;
      }, 100);
    }, {
      immediate: true
    });
    this.$once('hook:beforeDestroy', () => clearTimeout(weirdTimer));
  }
  mounted() {
    const {
      syncStyleDebounced
    } = this;
    const editZone = this.designer.$refs.editZone;
    editZone.$el.addEventListener('scroll', syncStyleDebounced, true);
    editZone.$el.addEventListener('mouseenter', syncStyleDebounced, true);
    this.$once('hook:beforeDestroy', () => {
      syncStyleDebounced.cancel();
      editZone.$el.removeEventListener('scroll', syncStyleDebounced, true);
      editZone.$el.removeEventListener('mouseenter', syncStyleDebounced, true);
    });
    this.syncStyle();
  }
  startDraggingBlock(ev) {
    var _ev$target, _ev$target$matches;
    if (!ev.dataTransfer) return;
    // TODO: 对于abs布局中的组件整体拖动也响应 添加一个快捷键? 如果按下cmd/ctrl的话 可以允许拖拓扑结构
    // 绝对定位也可以拖吧
    if (this.isInAbsoluteSlot && ev[CTRL_KEY]) {
      const absPosTool = this.$refs.absolutePositioningTool;
      ev.stopPropagation();
      ev.preventDefault();
      absPosTool.handleMoveMouseDown(ev);
      return;
    }
    if (!this.isDraggable) return;
    if ((_ev$target = ev.target) !== null && _ev$target !== void 0 && (_ev$target$matches = _ev$target.matches) !== null && _ev$target$matches !== void 0 && _ev$target$matches.call(_ev$target, '.draggable-ignore, .draggable-ignore *')) {
      ev.preventDefault();
      return;
    }
    const layout = this.layout;
    const dataPayload = JSON.stringify(layout, null, 2);
    // ev.dataTransfer.setDragImage(TRANSPARENT_IMAGE, 0, 0);
    // ev.dataTransfer.setDragImage(this.getCompEl(), ev.offsetX, ev.offsetY);
    ev.dataTransfer.setData('text/plain', dataPayload);
    ev.dataTransfer.setData(DND_TYPE_LAYOUT, dataPayload);
    ev.dataTransfer.effectAllowed = 'copyMove';
    useDragGhost(ev, this.uc.getLayoutName(layout));
    this.designer.setDragSource({
      layout,
      oldCompPath: this.compPath
    });
    ev.stopPropagation();
    return true;
  }
  stopDraggingBlock(ev) {
    ev.preventDefault();
    ev.stopPropagation();
    this.designer.setDragSource(null);
  }
  get canAdjustAbsolutePosition() {
    var _this$compMeta3;
    if (!this.compPath || (_this$compMeta3 = this.compMeta) !== null && _this$compMeta3 !== void 0 && _this$compMeta3.isUnselectable) return false;
    return this.compElement && this.isPositionAbsolute;
  }
  get portalPropsForDraggable() {
    const ans = {
      attrs: {
        draggable: this.isDraggable || this.canAdjustAbsolutePosition,
        'data-uc-path': this.compPath
      },
      on: {
        '!dragstart': this.startDraggingBlock,
        '!dragend': this.stopDraggingBlock,
        '!dragover': this.designer.editZoneState.handleEditZoneDragOverCapture,
        '!dragleave': this.designer.editZoneState.handleEditZoneDragLeaveCapture,
        '!drop': this.designer.editZoneState.handleEditZoneDropCapture
      }
    };
    if (this.canAdjustAbsolutePosition) {
      ans.on['!dragstart'] = ev => {
        const absPosTool = this.$refs.absolutePositioningTool;
        ev.stopPropagation();
        ev.preventDefault();
        absPosTool.handleMoveMouseDown(ev);
      };
    }
    return ans;
  }
  render() {
    const h = arguments[0];
    if (!this.isVisible) return null;
    const {
      DesignTool,
      actionBarStatus
    } = this;
    const {
      inspecting
    } = this.designer.editZoneState;
    return h("div", {
      "style": this.skeletonStyle,
      "class": `ucd-compSkeleton ${this.className}`,
      "attrs": {
        "data-uc-path": this.compPath
      }
    }, [this.isNestedRoot && h("div", {
      "class": "ucd-compSkeleton-outline",
      "attrs": {
        "data-type": "scopeRoot"
      }
    }), this.highlightType && h("div", {
      "class": "ucd-compSkeleton-outline",
      "attrs": {
        "data-type": this.highlightType
      }
    }), this.hasError && this.designer.skeletonStyle.showError && h("div", {
      "class": "ucd-compSkeleton-outline",
      "attrs": {
        "data-type": "hasError"
      }
    }), this.hasDisplayCondition && this.designer.skeletonStyle.showDisplayCondition && h("div", {
      "class": "ucd-compSkeleton-outline",
      "attrs": {
        "data-type": "hasDisplayCondition"
      }
    }), actionBarStatus !== 'none' && h(Portal, {
      "attrs": {
        "target": "editZone"
      }
    }, [h("div", _mergeJSXProps([{
      "class": mergeClassName('ucd-actionBar-wrapper', this.designer.isDragging && 'isDragging', !this.isSelected && 'isForHoveringComp', this.actionBarInBottom && 'ucd-actionBarInBottom'),
      "ref": "actionBarWrapper",
      "key": "actionBarWrapper",
      "style": this.actionBarWrapperStyle + (actionBarStatus === 'transparent' ? '; opacity: 0; ' : '') /* 拖拽期间隐藏，但是不要移除DOM，因为这个本身就可以是拖拽源 */
    }, this.portalPropsForDraggable]), [
    // eslint-disable-next-line no-nested-ternary
    !inspecting && (this.isSelected ? h(CompActionBar, {
      "on": {
        "updated": this.syncStyle
      }
    }) : h("div", {
      "class": "ucd-actionBar isForHoveringComp"
    }, [this.compName]))])]), this.isSelected && h(AbsolutePositioningTool, {
      "ref": "absolutePositioningTool",
      "attrs": {
        "ownerBlock": this
      }
    }), actionBarStatus !== 'none' && DesignTool && h(ErrorBoundary, {
      "key": "designToolWrapper",
      "class": "ucd-designToolWrapper",
      "nativeOn": {
        "mousedown": this.handleDesignToolMousedown
      }
    }, [h(DesignTool, {
      "class": "ucd-designTool",
      "attrs": {
        "compPath": this.compPath,
        "isHovering": this.isHovering
      }
    })])]);
  }
};
__decorate([CalmComputedGetter()], CompSkeleton.prototype, "compInstance", null);
__decorate([CalmComputedGetter()], CompSkeleton.prototype, "compType", null);
__decorate([CalmComputedGetter()], CompSkeleton.prototype, "isNestedRoot", null);
__decorate([CalmComputedGetter()], CompSkeleton.prototype, "compName", null);
__decorate([CalmComputedGetter()], CompSkeleton.prototype, "className", null);
__decorate([CalmComputedGetter()], CompSkeleton.prototype, "isTempHidden", null);
CompSkeleton = __decorate([Component({
  name: 'CompSkeleton',
  inject: {
    uc: {
      from: INJECT_UICORE_BUS
    },
    designer: {
      from: INJECT_UICORE_DESIGNER
    }
  },
  props: {
    compPath: String,
    highlightType: String
  }
})], CompSkeleton);
export default CompSkeleton;
//# sourceMappingURL=CompSkeleton.js.map
